import closeFill from '@iconify/icons-eva/close-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { Alert, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { IconButtonAnimate } from 'components/animate';
import { handleResetPassword } from 'external/firebase/controllers/auth';
import useLocales from 'hooks/useLocales';
import { PATH_AUTH } from 'routes/paths';

export interface NewPasswordFormValues {
    password: string;
}

interface NewPasswordFormProps {
    actionCode: string;
}

const NewPasswordForm: React.FunctionComponent<NewPasswordFormProps> = ({ actionCode }) => {
    const navigate = useNavigate();
    const { translate } = useLocales();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [showPassword, setShowPassword] = useState(false);

    const ResetPasswordRequestSchema = Yup.object().shape({
        password: Yup.string()
            .min(8, translate('password_must_be_at_least_8_characters'))
            .required(translate('password_required')),
    });

    const formik = useFormik({
        initialValues: {
            password: '',
        },
        validationSchema: ResetPasswordRequestSchema,
        onSubmit: async (
            values: NewPasswordFormValues,
            { setSubmitting, setStatus, resetForm }: FormikHelpers<NewPasswordFormValues>,
        ) => {
            const res = await handleResetPassword(actionCode, values.password);
            setSubmitting(false);
            if (!res.ok) {
                resetForm();
                setStatus(translate(res.code));
                return;
            }
            navigate(PATH_AUTH.login, { replace: true });
            enqueueSnackbar(translate('password_reset_success'), {
                variant: 'success',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        },
    });

    const { errors, status, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

    const handleShowPassword = () => {
        setShowPassword(show => !show);
    };

    return (
        <FormikProvider value={formik}>
            <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    {status && <Alert severity='error'>{status}</Alert>}

                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        label={translate('password')}
                        {...getFieldProps('password')}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <IconButton onClick={handleShowPassword} edge='end'>
                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                    />

                    <LoadingButton fullWidth size='large' type='submit' variant='contained' loading={isSubmitting}>
                        {translate('set_password')}
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};

export default NewPasswordForm;
